import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Card, CardContent, CircularProgress, Table, TableBody, TableCell, TableHead, TableRow, ToggleButton, ToggleButtonGroup, Tooltip, Typography } from '@mui/material';
import { statsService } from '../../services/api';

const PAGE_LABELS = {
  '/aujourdhui': 'Éphéméride du jour',
  '/dictionnaire': 'Dictionnaire',
  '/quiz': 'Quiz',
  '/editeur': 'Éditeur duala',
  '/subscribe': 'Inscription',
  autre: 'Autres pages',
};
const PERIODS = [7, 30, 90];
const fmtDay = (d) => new Date(`${d}T00:00:00`).toLocaleDateString('fr-FR', { day: '2-digit', month: '2-digit' });

function Kpi({ label, value, hint }) {
  return (
    <Card variant="outlined" sx={{ flex: '1 1 160px' }}>
      <CardContent>
        <Typography variant="caption" color="text.secondary">{label}</Typography>
        <Typography variant="h4" sx={{ fontWeight: 800 }}>{value}</Typography>
        {hint && <Typography variant="caption" color="text.secondary">{hint}</Typography>}
      </CardContent>
    </Card>
  );
}

// Barres par jour en CSS pur : aucune bibliotheque de graphiques a charger.
function DailyBars({ daily, days }) {
  const byDay = new Map(daily.map((d) => [d.day, d]));
  const series = [];
  for (let i = days - 1; i >= 0; i--) {
    const dt = new Date();
    dt.setDate(dt.getDate() - i);
    const key = `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, '0')}-${String(dt.getDate()).padStart(2, '0')}`;
    series.push(byDay.get(key) || { day: key, views: 0, visitors: 0 });
  }
  const max = Math.max(1, ...series.map((s) => s.views));
  return (
    <Box sx={{ display: 'flex', alignItems: 'flex-end', gap: days > 30 ? '1px' : '3px', height: 140, overflowX: 'auto', pb: 0.5 }} role="img" aria-label="Pages vues par jour">
      {series.map((s) => (
        <Tooltip key={s.day} title={`${fmtDay(s.day)} : ${s.views} pages vues, ${s.visitors} visiteur(s)`} arrow>
          <Box sx={{ flex: '1 0 6px', minWidth: 4, height: `${Math.max(2, (s.views / max) * 100)}%`, bgcolor: s.views ? 'primary.main' : 'action.disabledBackground', borderRadius: '2px 2px 0 0' }} />
        </Tooltip>
      ))}
    </Box>
  );
}

function Statistics() {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');

  const load = useCallback(async () => {
    setData(null);
    setError('');
    try {
      const res = await statsService.getSummary(days);
      setData(res.data);
    } catch (e) {
      setError(e.response?.data?.error || 'Impossible de charger les statistiques.');
    }
  }, [days]);
  useEffect(() => { load(); }, [load]);

  const visitorsOn = (device) => (data ? (data.devices.find((d) => d.device === device) || { visitors: 0 }).visitors : 0);
  const maxPage = data ? Math.max(1, ...data.pages.map((p) => p.views)) : 1;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 1, mb: 2 }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Fréquentation du portail</Typography>
        <ToggleButtonGroup size="small" exclusive value={days} onChange={(_, v) => v && setDays(v)}>
          {PERIODS.map((p) => <ToggleButton key={p} value={p} sx={{ textTransform: 'none' }}>{p} jours</ToggleButton>)}
        </ToggleButtonGroup>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {!data && !error && <Box sx={{ textAlign: 'center', py: 6 }}><CircularProgress /></Box>}

      {data && (
        <>
          {data.totals.views === 0 && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Aucune visite enregistrée sur cette période. Le suivi compte les pages vues du portail public (hors équipe connectée, hors robots).
            </Alert>
          )}
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
            <Kpi label="Pages vues" value={data.totals.views} hint={`sur ${data.days} jours`} />
            <Kpi label="Visiteurs distincts" value={data.totals.visitors} hint="un navigateur = un visiteur" />
            <Kpi label="Sur téléphone" value={visitorsOn('mobile')} hint="visiteurs" />
            <Kpi label="Sur ordinateur" value={visitorsOn('desktop')} hint="visiteurs" />
          </Box>

          {data.quiz && (
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 3 }}>
              <Kpi label="Parties de quiz" value={data.quiz.plays} hint={`${data.quiz.players} joueur(s) distinct(s)`} />
              <Kpi label="Score moyen" value={data.quiz.avg_percent === null ? '—' : `${data.quiz.avg_percent} %`} hint="de bonnes réponses" />
              <Kpi label="Scores parfaits" value={data.quiz.perfect} hint="parties sans erreur" />
            </Box>
          )}

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Pages vues par jour</Typography>
          <Card variant="outlined" sx={{ mb: 3 }}><CardContent><DailyBars daily={data.daily} days={data.days} /></CardContent></Card>

          <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Par page</Typography>
          <Card variant="outlined" sx={{ mb: 2, overflowX: 'auto' }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Page</TableCell>
                  <TableCell align="right">Pages vues</TableCell>
                  <TableCell align="right">Visiteurs</TableCell>
                  <TableCell sx={{ width: '35%' }} />
                </TableRow>
              </TableHead>
              <TableBody>
                {data.pages.map((p) => (
                  <TableRow key={p.path}>
                    <TableCell>{PAGE_LABELS[p.path] || p.path}</TableCell>
                    <TableCell align="right">{p.views}</TableCell>
                    <TableCell align="right">{p.visitors}</TableCell>
                    <TableCell><Box sx={{ height: 10, borderRadius: 5, bgcolor: 'primary.main', width: `${(p.views / maxPage) * 100}%`, minWidth: 4 }} /></TableCell>
                  </TableRow>
                ))}
                {data.pages.length === 0 && (
                  <TableRow><TableCell colSpan={4} align="center" sx={{ color: 'text.secondary' }}>Pas encore de données</TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
          <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 2 }}>
            <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Mots les plus cherchés</Typography>
              <Card variant="outlined" sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead><TableRow><TableCell>Mot</TableCell><TableCell align="right">Recherches</TableCell><TableCell align="right">Résultats</TableCell></TableRow></TableHead>
                  <TableBody>
                    {(data.top_searches || []).map((s) => (
                      <TableRow key={s.word}><TableCell>{s.word}</TableCell><TableCell align="right">{s.searches}</TableCell><TableCell align="right">{s.avg_results}</TableCell></TableRow>
                    ))}
                    {(data.top_searches || []).length === 0 && <TableRow><TableCell colSpan={3} align="center" sx={{ color: 'text.secondary' }}>Pas encore de recherches</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </Card>
            </Box>
            <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
              <Typography variant="subtitle1" sx={{ fontWeight: 700, mb: 1 }}>Recherches sans résultat</Typography>
              <Card variant="outlined" sx={{ overflowX: 'auto' }}>
                <Table size="small">
                  <TableHead><TableRow><TableCell>Mot cherché</TableCell><TableCell align="right">Fois</TableCell></TableRow></TableHead>
                  <TableBody>
                    {(data.missing_searches || []).map((s) => (
                      <TableRow key={s.word}><TableCell>{s.word}</TableCell><TableCell align="right">{s.searches}</TableCell></TableRow>
                    ))}
                    {(data.missing_searches || []).length === 0 && <TableRow><TableCell colSpan={2} align="center" sx={{ color: 'text.secondary' }}>Aucune</TableCell></TableRow>}
                  </TableBody>
                </Table>
              </Card>
              <Typography variant="caption" color="text.secondary">Mots que les visiteurs cherchent et que le dictionnaire ne contient pas : une piste pour l'enrichir.</Typography>
            </Box>
          </Box>

          <Typography variant="caption" color="text.secondary">
            Suivi anonyme : ni adresse IP, ni cookie. Un identifiant aléatoire stocké dans le navigateur permet seulement de compter les visiteurs distincts. Les recherches enregistrées ne contiennent que le mot tapé. Les visites et recherches de l'équipe connectée et des robots ne sont pas comptées.
          </Typography>
        </>
      )}
    </Box>
  );
}

export default Statistics;
